import { clsx } from 'clsx';
import { useFormik } from 'formik';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import { Link, useHistory, useParams } from 'react-router-dom';
import Select from 'react-select';
import { toast, ToastContainer, ToastOptions } from 'react-toastify';
import Input from '../../../components/Input';
import ModalDelete from '../../../components/ModalDelete';
import { ModalNewLoading } from '../../../components/ModalNewLoading/index';
import { useAuth } from '../../../contexts/auth';
import api from '../../../services/api';
import './styles.css';
import './styles.scss';

interface RouteParams {
  id: string;
}

export interface iOption {
  netCapacity?: number | undefined;
  label: string;
  value: string;
  vehicleModel?: {
    name: string;
  };
}

interface Customer {
  code: number;
  name: string;
  city: string;
}

interface IShippingCompany {
  id: number;
  code: number;
  name: string;
}
interface Company {
  id: number;
}

interface Product {
  code: number;
  name: string;
}
interface OrderItem {
  id: number;
  quantity: number;
  product: Product;
}

export interface Driver {
  id: number;
  name: string;
}
export interface Truck {
  id: number;
  licensePlate: string;
  netCapacity?: number;
}

interface TruckOption {
  id: number;
  name: string;
  licensePlate: string;
  netCapacity?: number;
}
interface _Scheduling {
  id: number;
  order: _Order;
  qtyOrdered: number;
  qtyTransported: number;
  expedDate?: any;
  expedInitialDate?: any;
  expirationDate?: null | string;
  driver: Driver;
  truck: Truck;
  company: Company;
  status?: boolean;
}

interface DropoutsSchedulings extends _Scheduling {
  reason: ReasonEnum;
}

interface SchedulingWithDaysLeft extends _Scheduling {
  daysLeft?: number;
}

interface _Order {
  id: number;
}

interface Order {
  id: number;
  code: string;
  enable: boolean;
  status: string;
  customer: Customer;
  shippingCompany: IShippingCompany[];
  product: Product;
  qntOrdered: number;
  qntShip: number;
  shippingCompanyFormatted: string;
  company: {
    id: number;
    name: string;
  };
  orderItem: OrderItem[];
  qtyTransported: number;
}

type SchedulingStatus = 'Aguardando' | 'Concluidos' | 'Desistentes';
type ModalType = 'add' | 'edit' | 'delete';
export type FocusedField = 'vehiclePlate' | 'driver' | '';

const toastOptions: ToastOptions = {
  position: 'top-right',
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
};

const Scheduling: React.FC = (): JSX.Element => {
  const [order, setOrder] = useState<Omit<Order, 'orderItem'>>();
  const [schedulings, setSchedulings] = useState<_Scheduling[]>([]);
  const [schedulingsDeleted, setSchedulingsDeleted] = useState<DropoutsSchedulings[]>([]);
  const [vehicleModel, setVehiculoModel] = useState('');
  const [schedulingStatus, setSchedulingStatus] = useState<SchedulingStatus>('Aguardando');

  const [licensePlateOptions, setLicensePlateOptions] = useState<iOption[]>([{ value: '', label: '' }]);
  const [licensePlateValue, setLicensePlateValue] = useState<Truck>({
    id: 0,
    licensePlate: '',
    netCapacity: 0,
  });
  const [selectedPlate, setSelectedPlate] = useState<any>();

  const [driverOptions, setDriverOptions] = useState<iOption[]>([{ value: '', label: '' }]);
  const [driverValue, setDriverValue] = useState<Driver>({ id: 0, name: '' });
  const [truckSelectedCompanyId, setTruckSelectedCompanyId] = useState<number | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<any>();

  const [expirationDate, setExpirationDate] = useState<string>(moment().format('YYYY-MM-DD'));
  const [quantity, setQuantity] = useState(0);
  const [totalShip, setTotalShip] = useState(order?.qtyTransported || 0);
  const [isFreelancerDriver, setIsFreelancerDriver] = useState(false);

  const [modal, setModal] = useState('');
  const [nameModal, setNameModal] = useState('');
  const [modalType, setModalType] = useState<ModalType>();
  const [schedulingId, setSchedulingId] = useState(0);
  const [key, setKey] = useState(0);

  const [inEditMode, setInEditMode] = useState({
    status: false,
    rowKey: 0,
  });

  const [focusedField, setFocusedField] = useState<FocusedField>('');

  const orderQuantity = order?.qntOrdered ?? 0;
  const orderTransported = order?.qtyTransported ?? 0;
  const totalDelivered = totalShip + orderTransported;
  const diffOrderDispatch = useMemo(() => {
    if (!orderQuantity) return 0;

    return orderQuantity - totalDelivered;
  }, [totalDelivered, orderQuantity]);

  const deliveredPercentage = useMemo(() => {
    if (!orderQuantity) return 0;
    return (totalDelivered / orderQuantity) * 100;
  }, [orderQuantity, totalDelivered]);

  const schedulingsWithDaysLeft = useMemo(() => {
    if (!schedulings.length) return [];

    const today = moment().startOf('day');
    return schedulings.map((scheduling) => {
      const expirationDate = moment(scheduling.expirationDate).startOf('day');

      return {
        ...scheduling,
        daysLeft: scheduling.expirationDate ? expirationDate.diff(today, 'days') : undefined,
      };
    });
  }, [schedulings]);

  // console.log({ orderQuantity, totalShip, orderTransported, diffOrderDispatch });

  const schedulingByCategory = useMemo(() => {
    const categorized = schedulingsWithDaysLeft.reduce(
      (acc: Record<SchedulingStatus, SchedulingWithDaysLeft[]>, scheduling) => {
        if (scheduling.status) {
          acc.Concluidos.push(scheduling);
        }

        if (scheduling.expedInitialDate === null) {
          acc.Aguardando.push(scheduling);
        }

        return acc;
      },
      { Aguardando: [], Concluidos: [], Desistentes: [] }
    );

    if (schedulingsDeleted.length > 0) {
      categorized.Desistentes = schedulingsDeleted;
    }

    return categorized;
  }, [schedulingsWithDaysLeft, schedulingsDeleted]);
  const schedulingCategoryKeys = Object.keys(schedulingByCategory) as SchedulingStatus[];

  const { handleSubmit } = useFormik({
    initialValues: {
      id: '',
      driver: { id: 0, name: '' },
      truck: { id: 0, licensePlate: '' },
      qtyOrdered: 0,
      qtyTransported: 0,
    },

    onSubmit: (values) => {
      api
        .post('scheduling/save', schedulings)
        .then(() => {
          toast.success('Cadastro realizado com sucesso', toastOptions);
          setTimeout(() => {
            history.push('/pedidos');
          }, 2500);
        })
        .catch((e) => {
          toast.error('Erro ao Cadastrar: ' + e.response.data.message, toastOptions);
        });
    },
  });

  const { id } = useParams<RouteParams>();

  const history = useHistory();
  const { role, company } = useAuth();
  const companyId = company || parseInt(JSON.stringify(localStorage.getItem('@App:companyId')));

  useEffect(() => {
    if (id) {
      api.get<Order>(`/order/${id}`).then((response) => {
        const { customer } = response.data;
        const { shippingCompany } = response.data;
        const { orderItem } = response.data;

        const qntOrdered = orderItem.reduce((sum: any, item: OrderItem) => {
          return sum + item.quantity;
        }, 0);

        const product = {
          code: orderItem[0]?.product?.code,
          name: orderItem[0]?.product?.name,
        };

        const shippings = shippingCompany.map((item: IShippingCompany) => item.name) as string[];

        setOrder({
          id: parseInt(id),
          code: response.data.code,
          enable: response.data.enable,
          customer: customer,
          shippingCompany: shippingCompany,
          status: parseFloat(response.data.status).toFixed(0),
          product: product,
          qntOrdered: qntOrdered,
          qntShip: 0,
          shippingCompanyFormatted: shippings.join(', '),
          company: response.data.company,
          qtyTransported: response.data.qtyTransported,
        });
      });
    }

    (async () => {
      const promises = [loadSchedules(), loadDropoutsSchedulings()];
      try {
        await Promise.allSettled(promises);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadDropoutsSchedulings() {
    try {
      const { data } = await api.get(`/schedulingDeleted/order/${id}`);
      const scheduling = data.map((item: DropoutsSchedulings) => {
        return {
          id: item.id,
          order: item.order,
          qtyOrdered: item.qtyOrdered,
          qtyTransported: item.qtyTransported,
          reason: item.reason,
          expedDate: item.expedDate ? item.expedDate : '',
          expedInitialDate: item.expedInitialDate ? item.expedInitialDate : null,
          expirationDate: item.expirationDate,
          driver: item.driver,
          truck: item.truck,
          status: item.status,
        };
      });

      setSchedulingsDeleted(scheduling);
    } catch (error) {
      console.error('Failed to load deleted schedules', error);
    }
  }

  async function loadSchedules() {
    try {
      const { data } = await api.get(`/scheduling/order/${id}`);
      let totalship = 0;
      const scheduling = data.map((item: _Scheduling) => {
        totalship += item.qtyTransported;
        return {
          id: item.id,
          order: item.order,
          qtyOrdered: item.qtyOrdered,
          qtyTransported: item.qtyTransported,
          expedDate: item.expedDate ? item.expedDate : '',
          expedInitialDate: item.expedInitialDate ? item.expedInitialDate : null,
          expirationDate: item.expirationDate,
          driver: item.driver,
          truck: item.truck,
          status: item.status,
        };
      });

      setSchedulings(scheduling);
      setTotalShip(totalship);
    } catch (error) {
      console.error('Failed to load schedules', error);
    }
  }

  function handleChangeInputSelect(value: string, patch: string, setOption: any) {
    let options_: iOption[] = [];
    const onlyStringAndNumbers = /^[a-zA-Z0-9]*$/;

    if (value) {
      if (!onlyStringAndNumbers.test(value) && value.length > 0 && focusedField === 'vehiclePlate') {
        if (!toast.isActive('valueValidation')) {
          toast.error('O valor deve conter apenas letras, números e sem espaços.', { ...toastOptions, toastId: 'valueValidation' });
        }
        return;
      }

      let path = `/${patch}/like/${value}`;

      if (!isFreelancerDriver) {
        if (role === 'Usuario') {
          path += `/company/${company}`;
        }

        if (role === 'Transportadora') {
          const shippingCompanyId = localStorage.getItem('@App:shippingCompanyId');
          path += `/shipping-company/${shippingCompanyId}`;
        }
      }

      api.get(path).then((response) => {
        // eslint-disable-next-line array-callback-return

        options_ = response.data.map((item: any) => {
          if (patch === 'truck') {
            return {
              value: item.id,
              label: `${item.licensePlate}`,
              vehicleModel: item.vehicleModel,
              netCapacity: item.netCapacity,
            };
          } else if (patch === 'driver') {
            return { value: item.id, label: `${item.name}` };
          } else {
            return '';
          }
        });

        if (isFreelancerDriver) {
          const valueLengthEqualToSeven = value.length === 7;
          if (!valueLengthEqualToSeven) {
            if (!toast.isActive('licensePlateError') && value.length > 0 && focusedField === 'vehiclePlate') {
              toast.error('Placa deve conter 7 caracteres', { ...toastOptions, toastId: 'licensePlateError' });
            }
          }

          setOption(options_);
          return;
        }
        setOption(options_);
      });
    }
  }
  function handleCorfirmDelete(scheduling: _Scheduling, index: number) {
    //! Medida adicional de segurança para evitar que o usuário exclua um agendamento que já foi expedido
    if (scheduling.expedInitialDate) return;

    showModal('delete');
    setNameModal(scheduling.driver.name);
    setSchedulingId(scheduling.id);
    setKey(index);
  }

  function handleChangeOptionLicensePlate(option: iOption) {
    setLicensePlateValue({
      id: parseInt(option.value),
      licensePlate: option.label,
      netCapacity: option.netCapacity,
    });

    setVehiculoModel(option.vehicleModel?.name || '');
    setQuantity(option.netCapacity || 0);
    setSelectedPlate({ value: parseInt(option.value), label: option.label });

    api.get(`/truck/${option.value}`).then((response: any) => {
      const driverId = response.data?.driver?.id;
      const driverCompanyId = response.data.company.id || company;

      setTruckSelectedCompanyId(driverCompanyId);

      if (driverId) {
        api.get(`/driver/company/${driverCompanyId}`).then((response) => {
          // eslint-disable-next-line array-callback-return

          const driversCompany = response.data;

          let driverObject = { value: '', label: '' };

          setDriverOptions([driverObject]);
          setSelectedDriver(driverObject);
          setDriverValue({
            id: parseInt(driverObject.value),
            name: driverObject.label,
          });

          driversCompany.filter((item: TruckOption) => {
            if (item.id === driverId) {
              driverObject = { value: item.id.toString(), label: item.name };

              setDriverOptions([driverObject]);
              setSelectedDriver(driverObject);
              setDriverValue({
                id: parseInt(driverObject.value),
                name: driverObject.label,
              });
            }

            return item.id === driverId;
          });
        });
      }
    });
  }

  useEffect(() => {
    if (selectedPlate) {
      setSelectedDriver({ value: '', label: '' });
      setDriverValue({ id: 0, name: '' });
    }
  }, [selectedPlate]);

  function handleChangeOptionDriver(option: iOption) {
    setSelectedDriver(option);
    setDriverValue({ id: parseInt(option.value), name: option.label });
  }

  function handleCloseModal() {
    hideModal();

    if (modalType === 'add') {
      reset();
    }
  }
  const showModal = (type: ModalType) => {
    const matchers: Record<ModalType, VoidFunction> = {
      add: () => {
        setModal('show');
        setModalType('add');
      },
      edit: () => {
        setModal('show');
        setModalType('edit');
      },
      delete: () => {
        setModal('show');
        setModalType('delete');
      },
    };
    const types = Object.keys(matchers);

    if (!types.includes(type)) return console.error(`Type: ${type}, not found in ${types.join(', ')}`);

    return matchers[type]();

    // setModal('show');
  };
  const hideModal = () => {
    setModal('');
  };

  const handleToggleFrelancerDriver = (value: boolean) => {
    setIsFreelancerDriver(value);
  };

  function reset() {
    setTruckSelectedCompanyId(null);
    setSelectedDriver({ id: -1, name: '' });
    setQuantity(0);
    setDriverOptions([]);
    setDriverValue({ id: 0, name: '' });
    setSelectedPlate({});
    handleChangeInputSelect('', 'truck', setLicensePlateOptions);
    handleChangeOptionLicensePlate({} as iOption);
    setIsFreelancerDriver(false);
    setExpirationDate('');
    setLicensePlateOptions([{ value: '', label: '' }]);
    setExpirationDate(moment().format('YYYY-MM-DD'));
  }

  // const data = {
  //   quantity,
  //   licensePlateValue,
  //   driverValue,
  //   expirationDate,
  // };
  async function handleAddScheduling() {
    const qntOrdered = schedulings.reduce((sum: any, item: any) => {
      return sum + item.qtyOrdered;
    }, 0);

    if (qntOrdered + quantity > (order?.qntOrdered || 0)) {
      toast.error('A Capacidade Líquida ultrapassa o Saldo do Pedido.', toastOptions);
      return;
    }

    if (quantity > (licensePlateValue.netCapacity || 0)) {
      toast.error('A quantidade ultrapassa a capacidade do veículo', toastOptions);
      return;
    }

    if (!licensePlateValue.licensePlate) return toast.error('Placa do veículo é obrigatória!', toastOptions);
    if (!driverValue.name) return toast.error('Nome do motorista é obrigatório!', toastOptions);
    if ((quantity && typeof quantity !== 'number') || isNaN(quantity)) {
      return toast.error('A quantidade deve ser um número!', toastOptions);
    }
    if (quantity <= 0) return toast.error('Precisa ser maior que zero!', toastOptions);
    if (!expirationDate) return toast.error('Data de validade é obrigatória!', toastOptions);
    if (moment(expirationDate).isBefore(moment().startOf('day'))) {
      return toast.error('A data de validade não pode ser anterior a hoje!', toastOptions);
    }

    try {
      const expirationDateFormatted = moment(expirationDate).toDate().toISOString();
      const postData = {
        id: 0,
        qtyOrdered: quantity,
        qtyTransported: 0,
        order: { id: parseInt(id) },
        truck: {
          id: licensePlateValue.id,
          licensePlate: licensePlateValue.licensePlate,
        },
        driver: { id: driverValue.id, name: driverValue.name },
        expirationDate: expirationDateFormatted,
      };
      // console.log(postData);

      const { data } = await api.get<{ orderHasThisDriver: boolean }>(`/order/${id}/hasDriver/${driverValue.id}`);
      const disabled = data.orderHasThisDriver;

      if (disabled) return toast.error('Motorista já vinculado ao pedido por outra transportadora.', toastOptions);

      await api.post('scheduling/save', [
        {
          ...postData,
          company: { id: truckSelectedCompanyId },
        },
      ]);

      toast.success('Cadastro realizado com sucesso', toastOptions);

      await loadSchedules();

      setSchedulings((prevState) => [
        ...prevState,
        {
          ...postData,
          company: { id: companyId },
        },
      ]);

      reset();
    } catch (e) {
      toast.error('Erro ao Cadastrar: ' + (e as any).response.data.message, toastOptions);
    } finally {
      setModal('');
    }
  }

  function handleChangeQuantity(event: React.ChangeEvent<HTMLInputElement>) {
    setQuantity(parseInt(event.target.value));
  }

  function handleExpirationDate(event: React.ChangeEvent<HTMLInputElement>) {
    const {
      target: { value },
    } = event;

    setExpirationDate(value);
  }

  function handleDeleteSchedule(id: number) {
    hideModal();

    if (id) {
      api
        .delete(`/scheduling/delete/${id}`)
        .then((response) => {
          if (response.status === 200) {
            toast.success('Excluido com sucesso!');
            const results = schedulings.filter((item) => item.id !== id);
            setSchedulings(results);

            api.post('scheduling/save', results);
          }
        })
        .catch(() => {
          toast.success('Erro ao excluir!');
        });
    } else {
      schedulings.splice(key, 1);
      setSchedulings(schedulings);
    }
  }

  function onEdit(id: number) {
    if (inEditMode.status === false) {
      setInEditMode({
        status: true,
        rowKey: id,
      });
    } else {
      const newSchedules = schedulings.map((item, index) => {
        if (index === id) {
          item.expedDate = null;

          return item;
        } else return item;
      });
      //schedulings

      api
        .post('scheduling/save', newSchedules)
        .then(() => {
          toast.success('Cadastro editado com sucesso');
        })
        .catch((e) => {
          toast.error('Erro ao Cadastrar: ' + e.response.data.message);
        });

      setInEditMode({
        status: false,
        rowKey: id,
      });
    }
  }

  async function handleEditTruck(id: number, option: iOption) {
    const newSchedulings = schedulings.map((item) => {
      if (item.truck.id === id) {
        item.truck.id = parseInt(option.value);
        item.truck.licensePlate = option.label;

        return item;
      } else return item;
    });
    setSchedulings(newSchedulings);
  }
  async function handleEditDriver(id: number, option: iOption) {
    const newSchedulings = schedulings.map((item) => {
      if (item.driver.id === id) {
        item.driver.id = parseInt(option.value);
        item.driver.name = option.label;

        return item;
      } else return item;
    });
    setSchedulings(newSchedulings);
  }
  async function handleQtyOrdered(id: number, value: string) {
    const newSchedulings = schedulings.map((item) => {
      if (item.id === id) {
        item.qtyOrdered = parseInt(value);
        return item;
      } else return item;
    });
    setSchedulings(newSchedulings);
  }

  function handleScheduleStatus(status: SchedulingStatus) {
    setSchedulingStatus(status);
  }
  function onCheckStatus(scheduling: SchedulingStatus) {
    return schedulingStatus === scheduling;
  }

  function renderByStatus(status: SchedulingStatus) {
    return schedulingStatus === status;
  }

  function handleAddVehicle() {
    if (!order?.enable) {
      if (toast.isActive('orderDisabled')) return;
      toast.error('Pedido desativado!', { position: 'top-right', toastId: 'orderDisabled' });
      return;
    }

    showModal('add');
  }

  return (
    <div className="new-scheduling-container">
      {modalType === 'delete' && (
        <ModalDelete type="Agendamento " name={nameModal} id={schedulingId} className={modal} handleClose={handleCloseModal} handleDeleteAction={handleDeleteSchedule} />
      )}

      {modalType === 'add' && (
        <ModalNewLoading
          className={modal}
          handleClose={handleCloseModal}
          handleAddScheduling={handleAddScheduling}
          handleToggleFrelancerDriver={handleToggleFrelancerDriver}
          handleChangeInputSelect={handleChangeInputSelect}
          handleChangeOptionDriver={handleChangeOptionDriver}
          handleChangeOptionLicensePlate={handleChangeOptionLicensePlate}
          handleChangeQuantity={handleChangeQuantity}
          handleExpirationDate={handleExpirationDate}
          isFreelancerDriver={isFreelancerDriver}
          expirationDate={expirationDate}
          licensePlateOptions={licensePlateOptions}
          driverOptions={driverOptions}
          selectedPlate={selectedPlate}
          selectedDriver={selectedDriver}
          driverValue={driverValue}
          vehicleModel={vehicleModel}
          quantity={quantity}
          licensePlateValue={licensePlateValue}
          setDriverOptions={setDriverOptions}
          setLicensePlateOptions={setLicensePlateOptions}
          setFocusedField={setFocusedField}
        />
      )}

      <form onSubmit={handleSubmit} autoComplete="off">
        <header className="scheduling__header">
          <div className="scheduling__header--title">
            <strong>Carregamentos {order?.code && `- ${order?.code}`}</strong>
            <span className={clsx(['scheduling__header--status', order?.enable && 'enabled'])}></span>
          </div>

          <div className="buttons-header">
            <Link to="/pedidos">
              <button id="btBack" type="button">
                <MdKeyboardArrowLeft size={20} color="#fff" />
                Voltar
              </button>
            </Link>
          </div>
        </header>

        <div className="schedulings__details">
          <div className="order__wrapper">
            <dl>
              <dt id="destinatario">Destinatário:</dt>
              <dd aria-labelledby="destinatario">{order?.customer?.name}</dd>
            </dl>

            <dl>
              <dt id="empresa">Empresa</dt>
              <dd aria-labelledby="empresa">{order?.company?.name}</dd>
            </dl>

            <dl>
              <dt id="municipio">Município:</dt>
              <dd aria-labelledby="municipio">{order?.customer?.city}</dd>
            </dl>

            <dl>
              <dt id="produto">Produto:</dt>
              <dd aria-labelledby="produto" style={{ marginBottom: 16 }}>
                {order?.product?.name}
              </dd>
            </dl>
          </div>

          <div className="product__info">
            <div className="product__info--block total">
              <p>
                <strong>{deliveredPercentage.toFixed(2)}</strong>%
              </p>

              <div className="product__info--progress" style={{ right: `calc(${100 - deliveredPercentage}% + 6px)` }}></div>
            </div>

            <div className="product__info--block requested">
              <div className="product__block--value gray">
                <p>{order?.qntOrdered || 'N/A'}</p>
              </div>

              <p>Pedido</p>
            </div>

            <div className="product__info--block dispatched">
              <div title={totalDelivered.toString()} className="product__block--value green">
                <p>{totalDelivered.toFixed(2)}</p>
              </div>

              <p>Expedido</p>
            </div>

            <div className="product__info--block balance">
              <div className={clsx(['product__block--value', diffOrderDispatch !== 0 && 'blue', diffOrderDispatch === 0 && 'red'])}>
                <p>{diffOrderDispatch.toFixed(2)}</p>
              </div>

              <p>Saldo</p>
            </div>
          </div>
        </div>

        <hr className="schedulings__divider" style={{ marginTop: 40, marginBottom: 15 }} />

        <dl>
          <dt id="transportadoras">Transportadora(s):</dt>
          {order?.shippingCompany.map((item) => (
            <dd aria-labelledby="transportadoras" className="carrier-chip" key={item.id}>
              {item.name}
            </dd>
          ))}
        </dl>

        <hr className="schedulings__divider" style={{ marginBlock: 15 }} />

        <div className="schedulings__loading">
          <div className="schedulings__head">
            <div className="status">
              {schedulingCategoryKeys.map((status, i) => (
                <button key={i} type="button" onClick={() => handleScheduleStatus(status)} className={clsx([`status__btn ${status}`, onCheckStatus(status) && 'active'])}>
                  {status}
                </button>
              ))}
            </div>

            <button type="button" onClick={handleAddVehicle} className="add-vehicle" disabled={!order?.enable}>
              + Adicionar Veículo
            </button>
          </div>

          <div className="schedulings__wrapper">
            <table className="schedulings__table">
              <colgroup>
                <col style={{ width: '10%' }} />
                <col style={{ width: '30%' }} />
                <col style={{ width: '10%' }} />
                {renderByStatus('Concluidos') && <col style={{ width: '20%' }} />}
                {renderByStatus('Aguardando') && <col style={{ width: '20%' }} />}
                {renderByStatus('Aguardando') && <col style={{ width: '10%' }} />}
              </colgroup>

              <thead>
                <tr>
                  <th className="cell cell__th">
                    <div className="cell__th--content">
                      <p>Placa</p>
                    </div>
                  </th>

                  <th className="cell cell__th">
                    <div className="cell__th--content">
                      <p>Motorista</p>
                    </div>
                  </th>

                  <th className="cell cell__th cell__align--center">
                    <div className="cell__th--content">
                      <p>Qtd. Entregue</p>
                    </div>
                  </th>

                  {renderByStatus('Concluidos') && (
                    <th className="cell cell__th cell__align--center">
                      <div className="cell__th--content">
                        <p>Data e Hora</p>
                      </div>
                    </th>
                  )}

                  {renderByStatus('Aguardando') && (
                    <th className="cell cell__th cell__align--center">
                      <div className="cell__th--content">
                        <p>Dias Restantes</p>
                      </div>
                    </th>
                  )}

                  {renderByStatus('Aguardando') && (
                    <th className="cell cell__th">
                      <div className="cell__th--content">
                        <p>Ação</p>
                      </div>
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {schedulingByCategory[schedulingStatus]?.length > 0 && (
                  <>
                    {schedulingByCategory[schedulingStatus].map((scheduling, index) => (
                      <tr key={index}>
                        <td className="cell cell__td board">
                          <div className="cell__td--content">
                            {inEditMode.status && inEditMode.rowKey === index ? (
                              <div className="select-truck">
                                <Select
                                  className="select-options"
                                  options={licensePlateOptions}
                                  onInputChange={(value) => handleChangeInputSelect(value, 'truck', setLicensePlateOptions)}
                                  onChange={(option) => handleEditTruck(scheduling.truck.id, option as iOption)}
                                />
                              </div>
                            ) : (
                              <p>{scheduling.truck.licensePlate}</p>
                            )}
                          </div>
                        </td>

                        <td className="cell cell__td driver">
                          <div className="cell__td--content">
                            {inEditMode.status && inEditMode.rowKey === index ? (
                              <div className="select-driver">
                                <Select
                                  className="select-options"
                                  options={driverOptions}
                                  onInputChange={(value) => handleChangeInputSelect(value, 'driver', setDriverOptions)}
                                  onChange={(option) => handleEditDriver(scheduling.driver.id, option as iOption)}
                                />
                              </div>
                            ) : (
                              <p>{scheduling?.driver?.name}</p>
                            )}
                          </div>
                        </td>

                        <td className="cell cell__td cell__align--center quantity-ordered">
                          <div className="cell__td--content">
                            {inEditMode.status && inEditMode.rowKey === index ? (
                              <div className="select-qtyOrdered">
                                <Input
                                  className="select-options"
                                  type="number"
                                  value={scheduling.qtyTransported}
                                  onChange={(e) => handleQtyOrdered(scheduling.id, e.target.value)}
                                />
                              </div>
                            ) : (
                              <p>
                                {scheduling.qtyTransported?.toLocaleString('pt-BR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                                t
                              </p>
                            )}
                          </div>
                        </td>

                        {renderByStatus('Concluidos') && (
                          <td className="cell cell__td cell__align--center date">
                            <div className="cell__td--content">
                              <p>{scheduling.expedDate ? moment(scheduling.expedDate).format('DD/MM/YYYY HH:mm') : '-'}</p>
                            </div>
                          </td>
                        )}

                        {renderByStatus('Aguardando') && (
                          <td className="cell cell__td cell__align--center remaining-days">
                            {scheduling?.daysLeft && (
                              <div className="cell__td--content">
                                <p style={{ color: scheduling.daysLeft <= 3 ? 'red' : 'black' }}>
                                  {Math.abs(scheduling.daysLeft)} {`Dia${Math.abs(scheduling.daysLeft) > 1 ? 's' : ''}`}
                                </p>
                              </div>
                            )}
                          </td>
                        )}

                        {renderByStatus('Aguardando') && (
                          <td className="cell cell__td actions">
                            <div className="cell__td--content">
                              {!scheduling.expedDate && (
                                <>
                                  {role !== 'Transportadora' && (
                                    <button type="button" className="action-edit" onClick={() => onEdit(index)}>
                                      editar
                                    </button>
                                  )}

                                  {scheduling.expedInitialDate === null && (role !== 'Transportadora' || (scheduling.daysLeft !== undefined && scheduling.daysLeft > 0)) && (
                                    <button
                                      type="button"
                                      disabled={scheduling.expedInitialDate}
                                      className="action-delete"
                                      style={{ color: '#FFF' }}
                                      onClick={() => {
                                        handleCorfirmDelete(scheduling, index);
                                      }}
                                    >
                                      excluir
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </>
                )}

                {schedulingByCategory[schedulingStatus]?.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center' }}>
                      <div className="cell__td--content">Nenhum resultado foi encontrado</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {renderByStatus('Concluidos') && (
              <table className="schedulings__table overview">
                <colgroup>
                  <col style={{ width: '70%' }} />
                  <col style={{ width: '30%' }} />
                </colgroup>

                {/* <thead>
                  <tr>
                    <th className="cell cell__th cell__align--right"></th>

                    <th className="cell cell__th cell__align--right">
                      <div className="cell__th--content">
                        <p>Expedido</p>
                      </div>
                    </th>
                  </tr>
                </thead> */}

                <tbody>
                  {/* {schedulingByCategory.Concluidos.map((item, i) => (
                    <tr key={i}>
                      <td className="cell cell__td cell__align--right"></td>

                      <td className="cell cell__td cell__align--right">
                        <div className="cell__td--content">
                          <p>{item.qtyOrdered}</p>
                        </div>
                      </td>
                    </tr>
                  ))} */}

                  <tr>
                    <td className="cell cell__td cell__align--right">
                      <div className="cell__td--content">
                        <p>Total Expedido/Concluído:</p>
                      </div>
                    </td>

                    <td className="cell cell__td cell__align--right">
                      <p>
                        {schedulingByCategory.Concluidos.reduce((acc, item) => acc + item.qtyTransported, 0).toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>

        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </form>

      <br />
    </div>
  );
};

export default Scheduling;
